/**
 * Hardcoded Intelligence Insights Overview data for UI design.
 * Swap OVERVIEW_DEMO_VARIANT (or use the in-page demo selector) to preview Figma states.
 * Wire to API later.
 */

export const OVERVIEW_VARIANTS = {
    NOT_GENERATED: 'not_generated',
    NO_READY_SOURCES: 'no_ready_sources',
    GENERATED: 'generated',
    WARNINGS: 'warnings',
    NEW_EVIDENCE: 'new_evidence',
};

/** Change this to set the default preview state. */
export const OVERVIEW_DEMO_VARIANT = OVERVIEW_VARIANTS.GENERATED;

export const OVERVIEW_VARIANT_OPTIONS = [
    { value: OVERVIEW_VARIANTS.NOT_GENERATED, label: 'First use · not generated' },
    { value: OVERVIEW_VARIANTS.NO_READY_SOURCES, label: 'No ready sources' },
    { value: OVERVIEW_VARIANTS.GENERATED, label: 'Fully generated' },
    { value: OVERVIEW_VARIANTS.WARNINGS, label: 'With warnings' },
    { value: OVERVIEW_VARIANTS.NEW_EVIDENCE, label: 'New evidence' },
];

export const INSIGHT_MODULES = [
    {
        key: 'highlights',
        title: 'Highlights',
        category: 'reporting',
        description: 'Headline conclusions across Team, Technology and Cybersecurity.',
        metric: '3 areas assessed',
        status: 'complete',
    },
    {
        key: 'management_summary',
        title: 'Management Summary',
        category: 'reporting',
        description: 'Overall assessment, priority takeaways and eight-area RAG view.',
        metric: null,
        ragPill: { label: 'Amber', tone: 'amber' },
        metricPrefix: 'Overall RAG ·',
        status: 'complete',
    },
    {
        key: 'key_findings',
        title: 'Key Findings',
        category: 'reporting',
        description: 'Detailed assessment across eight technology areas.',
        metric: '8 areas assessed',
        status: 'complete',
    },
    {
        key: 'swot_analysis',
        title: 'SWOT Analysis',
        category: 'strategic_analysis',
        description: 'Evidence-backed strategic strengths, weaknesses, opportunities and threats.',
        metric: '14 material items',
        status: 'complete',
    },
    {
        key: 'investment_opinion',
        title: 'Investment Opinion',
        category: 'strategic_analysis',
        description: 'Technology-led interpretation of the transaction case.',
        metric: null,
        posturePill: { label: 'Deal posture · Conditional', tone: 'amber' },
        status: 'complete',
    },
    {
        key: 'remediation_investment',
        title: 'Remediation & Investment',
        category: 'strategic_analysis',
        description: 'Recommended remediation, timing and indicative investment arising from the current analysis.',
        metric: '3 high-priority treatments',
        status: 'complete',
    },
    {
        key: 'risks_signals',
        title: 'Risks & Signals',
        category: 'validation',
        description: 'Material technology risks and warning indicators.',
        metric: '6 material risks',
        status: 'complete',
    },
    {
        key: 'evidence_follow_ups',
        title: 'Evidence & Follow-Ups',
        category: 'validation',
        description: 'Evidence coverage and required follow-ups.',
        metric: '12 open matters',
        confidence: 'Medium confidence · Partial evidence',
        status: 'warnings',
        warningNote: '1 material source could not be processed.',
    },
];

export const MODULE_CATEGORY_ORDER = [
    { key: 'reporting', label: 'Reporting', columns: 3 },
    { key: 'strategic_analysis', label: 'Strategic Analysis', columns: 3 },
    { key: 'validation', label: 'Validation', columns: 2 },
];

const BASE_META = {
    contextLabel: 'Entire Project',
    versionLabel: 'Version 3',
    generatedLabel: 'Generated 27 Jul 2026',
    subtitle:
        'Evidence-backed technology analysis across reporting, strategic assessment and validation.',
    modulesAvailable: '8 of 8',
    modulesStat: { value: '8 / 8', label: 'Modules', sublabel: 'available' },
    readyStat: { value: '80', label: 'Ready', sublabel: 'sources' },
    warningStat: { value: '1', label: 'Warning', sublabel: null },
    confidence: 'Medium confidence · Partial evidence',
    analysisContext: {
        title: 'Entire Project',
        description:
            'All ready VDR documents, IRL responses and consultant observations across every workstream.',
        breakdown: 'VDR 42 · IRL 24 · Observations 14',
    },
    sourceReadiness: {
        ready: 72,
        processing: 4,
        failed: 1,
        note: 'Processing and failed sources were excluded from version 3.',
    },
    shellSources: { ready: 72, processing: 4 },
};

function withModuleOverrides(overrides = {}) {
    return INSIGHT_MODULES.map(mod => ({
        ...mod,
        ...(overrides[mod.key] || {}),
    }));
}

/**
 * Legacy demo-only overlay fixtures. Live Overview uses API
 * `data.secondary_views` via overviewPayloadToViewModel → secondaryViews.
 * Kept only so getOverviewDummyData demo mode can populate overlays.
 */
export const OVERVIEW_UI_DUMMY = {
    versions: [],
    analysisDetails: [],
    moduleConfig: INSIGHT_MODULES.map(mod => ({
        key: mod.key,
        title: mod.title,
        description: mod.description,
        projectType: 'Technology Due Diligence — M&A',
        enabled: true,
    })),
    generateModal: {
        title: 'Generate new analysis version?',
        rows: [],
        note: 'Demo mode — API generate is disabled.',
        confirmLabel: 'Generate →',
    },
    sourceReadiness: {
        summary: { ready: 0, processing: 0, failed: 0 },
        tabs: ['All', 'Ready', 'Processing', 'Failed', 'Material impact'],
        sources: [],
    },
};

export function getOverviewDummyData(variant = OVERVIEW_DEMO_VARIANT) {
    switch (variant) {
        case OVERVIEW_VARIANTS.NOT_GENERATED:
            return {
                variant,
                hasAnalysis: false,
                metaLine: 'Entire Project · Not yet generated',
                primaryActionLabel: 'Generate analysis',
                generateCta: {
                    title: 'Turn project evidence into structured intelligence',
                    description:
                        'Lens will analyse ready VDR documents, IRL responses and consultant observations to create seven evidence-backed insight modules.',
                    rows: [
                        { label: 'Selected context', value: 'Entire Project' },
                        { label: 'Source readiness', value: '80 ready · 4 processing · 1 failed' },
                        { label: 'Enabled modules', value: '7' },
                    ],
                    note: 'Processing and failed sources will be excluded.',
                },
                modules: withModuleOverrides(),
                shellSources: { ready: 80, processing: 4 },
            };

        case OVERVIEW_VARIANTS.NO_READY_SOURCES:
            return {
                variant,
                hasAnalysis: false,
                metaLine: 'Entire Project · Not yet generated',
                primaryActionLabel: 'Generate analysis',
                noReadySources: {
                    title: 'NO READY SOURCES',
                    status: 'UNAVAILABLE',
                    message:
                        'Lens cannot generate Intelligence Insights because no sources are currently ready for analysis.',
                    readiness: '0 ready · 12 processing · 1 failed',
                    note: 'Processing sources will be included once ready.',
                    actionLabel: 'Review project sources',
                },
                modules: withModuleOverrides({
                    highlights: { status: 'pending' },
                    management_summary: { status: 'pending' },
                    key_findings: { status: 'pending' },
                    swot_analysis: { status: 'pending' },
                    investment_opinion: { status: 'pending' },
                    risks_signals: { status: 'pending' },
                    evidence_follow_ups: { status: 'pending', warningNote: null },
                }),
                shellSources: { ready: 0, processing: 12 },
            };

        case OVERVIEW_VARIANTS.WARNINGS:
            return {
                variant,
                hasAnalysis: true,
                ...BASE_META,
                metaLine: 'Entire Project · Version 3 · Generated 27 Jul 2026',
                primaryActionLabel: 'Generate new version',
                analysisStatus: {
                    key: 'warnings',
                    label: 'Complete with warnings',
                },
                warningBanner: {
                    title: 'Analysis complete with warnings',
                    message: 'The analysis is available, but one material source could not be processed.',
                    source: 'Recovery Test Results.pdf · VDR · Scale & Resilience',
                    affected:
                        'Potentially affected: Scale & Resilience, Risks & Signals, Investment Opinion, Evidence & Follow-Ups',
                    actions: [
                        { key: 'review_failed', label: 'Review failed source' },
                        { key: 'retry', label: 'Retry processing' },
                        { key: 'open_analysis', label: 'Open current analysis' },
                    ],
                },
                modules: withModuleOverrides(),
            };

        case OVERVIEW_VARIANTS.NEW_EVIDENCE:
            return {
                variant,
                hasAnalysis: true,
                ...BASE_META,
                metaLine: 'Entire Project · Version 3 · Generated 27 Jul 2026',
                primaryActionLabel: 'Generate new version',
                analysisStatus: {
                    key: 'complete',
                    label: 'Complete',
                },
                newEvidenceBanner: {
                    title: 'New evidence available',
                    message: '9 sources have become ready since version 3 was generated.',
                    affected:
                        'Potentially affected: Scale & Resilience, Compliance & Cybersecurity, IT Budget, Investment Opinion',
                    note: 'Current outputs remain unchanged.',
                    actions: [
                        { key: 'review_evidence', label: 'Review new evidence' },
                        { key: 'generate_v4', label: 'Generate version 4' },
                    ],
                },
                modules: withModuleOverrides({
                    investment_opinion: {
                        status: 'new_evidence',
                        openLabel: 'Open current →',
                        secondaryActionLabel: 'Generate new version',
                    },
                }),
            };

        case OVERVIEW_VARIANTS.GENERATED:
        default:
            return {
                variant: OVERVIEW_VARIANTS.GENERATED,
                hasAnalysis: true,
                ...BASE_META,
                metaLine: 'Entire Project · Version 3 · Generated 27 Jul 2026',
                primaryActionLabel: 'Generate new version',
                analysisStatus: {
                    key: 'complete',
                    label: 'Complete',
                },
                modules: withModuleOverrides(),
            };
    }
}
